/* eslint-disable react-refresh/only-export-components -- shared invitation presentation helpers and badges */
import { Badge } from '../../../components/ui/badge'
import { useLocaleStore, type Locale } from '../../../store/locale'
import type { Invitation, InvitationStatus } from '../../../mocks/data/onboarding/invitations'

export type InvitationGroup = 'team' | 'internal' | 'seller' | 'buyer' | 'supply'
export type InvitationSource = 'simulation' | 'seed' | 'unverified'

export function getInvitationGroup(inv: Invitation): InvitationGroup {
  if (inv.org_type === 'A1' || inv.org_type === 'A2') return 'team'
  if (inv.org_type === 'BO1') return 'internal'
  if (inv.org_type === 'BO2') return 'seller'
  if (inv.org_type === 'BO3') return 'buyer'
  return 'supply'
}

export function getInvitationTypeLabel(inv: Invitation, locale: Locale) {
  const labels: Record<string, [string, string]> = {
    A1: ['Yönetim ekibi', 'Management team'], A2: ['Yönetim ekibi', 'Management team'],
    BO1: ['İç ticaret', 'Internal commerce'], BO2: ['Satıcı', 'Seller'], BO3: ['B2B alıcı', 'B2B buyer'],
    SO1: ['Kendi üretimimiz', 'Own production'], SO2: ['Üretim ortağı', 'Production partner'], SO3: ['Tedarikçi', 'Vendor'],
  }
  return labels[inv.org_type]?.[locale === 'tr' ? 0 : 1] ?? inv.org_type
}

export function getInvitationRoleLabel(inv: Invitation, locale: Locale) {
  const labels: Record<string, [string, string]> = {
    a1_platform_owner: ['Platform sahibi', 'Platform owner'], a2_internal_staff: ['Ekip üyesi', 'Team member'],
    owner: ['Firma sahibi', 'Organization owner'], so_owner: ['Firma sahibi', 'Organization owner'],
    admin: ['Yönetici', 'Administrator'], member: ['Üye', 'Member'], designer: ['Tasarımcı', 'Designer'],
    customer_service: ['Müşteri hizmetleri', 'Customer service'],
  }
  return labels[inv.initial_tier]?.[locale === 'tr' ? 0 : 1] ?? (locale === 'tr' ? 'Özel yetki' : 'Custom access')
}

// Transport mode is the only evidence of browser simulation. For API records,
// only the server's positive seed classification is evidence of example data.
export function getInvitationSource(inv: Invitation, realApi: boolean): InvitationSource {
  return !realApi ? 'simulation' : inv.data_source === 'seed' ? 'seed' : 'unverified'
}
function getInvitationSourceLabel(source: InvitationSource, locale: Locale) {
  const labels: Record<InvitationSource, [string, string]> = {
    simulation: ['Örnek veri', 'Simulated data'], seed: ['Örnek kayıt', 'Seeded example'],
    unverified: ['Kaynak doğrulanmadı', 'Source unverified'],
  }
  return labels[source][locale === 'tr' ? 0 : 1]
}
export function isExampleInvitation(inv: Invitation, realApi: boolean) {
  return getInvitationSource(inv, realApi) !== 'unverified'
}
export function InvitationStatusBadge({ status, category }: { status: InvitationStatus; category?: string }) {
  const locale = useLocaleStore(s => s.locale)
  const labels: Record<InvitationStatus, [string, string]> = {
    pending: ['Bekliyor', 'Pending'], sent: ['Bekliyor', 'Pending'], accepted: ['Kabul edildi', 'Accepted'],
    expired: ['Süresi doldu', 'Expired'], revoked: ['İptal edildi', 'Revoked'],
  }
  const direct = category === 'direct_create' && status === 'accepted'
  const label = direct ? (locale === 'tr' ? 'Doğrudan açıldı' : 'Created directly') : labels[status][locale === 'tr' ? 0 : 1]
  return <Badge variant={status === 'accepted' ? 'success' : status === 'expired' ? 'warning' : status === 'revoked' ? 'neutral' : 'info'}>{label}</Badge>
}
export function InvitationSourceBadge({ invitation, realApi }: { invitation: Invitation; realApi: boolean }) {
  const locale = useLocaleStore(s => s.locale)
  const source = getInvitationSource(invitation, realApi)
  return <Badge variant={source === 'unverified' ? 'neutral' : 'warning'}>{getInvitationSourceLabel(source, locale)}</Badge>
}
